import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";

import Banner1 from "../../../assets/img/website-images/Blogdetailsbanner1.png";
import Banner2 from "../../../assets/img/website-images/Blogdetailsbanner2.png";
import Banner3 from "../../../assets/img/website-images/Blogdetailsbanner3.png";

/* ─── Events Data ──────────────────────────────────────────────── */
const events = [
    {
        id: 1,
        date: "10 March, 2025",
        title: "Top Career Opportunities After B.Pharm: What You Can Become",
        image: Banner1,
        description:
            "A B.Pharm degree opens doors to a wide range of rewarding careers in healthcare, research, manufacturing, and beyond. Here is what you need to know.",
        time: "5 min read",
        slug: "/blogs/top-career-opportunities-after-bpharm",
    },
    {
        id: 2,
        date: "18 March, 2025",
        title: "Why Practical Training Matters More Than Ever in Pharmacy Education",
        image: Banner2,
        description:
            "Modern pharmacy is no longer just about dispensing medicine. Hands-on lab training is shaping the next generation of healthcare professionals.",
        time: "4 min read",
        slug: "/blogs/why-practical-training-matters-in-pharmacy-education",
    },
    {
        id: 3,
        date: "25 March, 2025",
        title: "Understanding the Role of a Pharmacist in Today's Healthcare System",
        image: Banner3,
        description:
            "Pharmacists are no longer just medicine dispensers — they are critical healthcare advisors, researchers, and patient safety champions.",
        time: "6 min read",
        slug: "/blogs/role-of-a-pharmacist-in-todays-healthcare-system",
    },
];

/* ─── Variants ─────────────────────────────────────────────────── */
const fadeDown = {
    hidden: { opacity: 0, y: -40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
    },
};

const cardContainerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.18, delayChildren: 0.2 } },
};

const cardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.95 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
};

const btnVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.55 },
    },
};

/* ─── Blog Card ────────────────────────────────────────────────── */
function BlogCard({ event }) {
    const imgRef = useRef(null);
    const overlayRef = useRef(null);

    /* GSAP: image zoom on hover */
    const handleMouseEnter = () => {
        gsap.to(imgRef.current, { scale: 1.07, duration: 0.5, ease: "power2.out" });
        gsap.to(overlayRef.current, { opacity: 1, duration: 0.4 });
    };
    const handleMouseLeave = () => {
        gsap.to(imgRef.current, { scale: 1, duration: 0.5, ease: "power2.out" });
        gsap.to(overlayRef.current, { opacity: 0, duration: 0.4 });
    };

    return (
        <motion.div
            variants={cardVariants}
            className="col-lg-4 col-md-6"
            style={{ marginBottom: "0px" }}
        >
            <div
                className="event-item mb-30"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{ overflow: "hidden", borderRadius: "10px" }}
            >
                {/* Thumbnail with GSAP zoom */}
                <div className="thumb" style={{ overflow: "hidden", position: "relative" }}>
                    <Link to={event.slug}>
                        <img
                            ref={imgRef}
                            src={event.image}
                            alt={event.title}
                            style={{ display: "block", width: "100%", transformOrigin: "center" }}
                        />
                    </Link>
                    {/* Hover overlay */}
                    <div
                        ref={overlayRef}
                        style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(30, 90, 168, 0.18)",
                            opacity: 0,
                            pointerEvents: "none",
                        }}
                    />
                </div>

                {/* Content */}
                <div className="event-content">
                    <motion.div
                        className="date"
                        initial={{ opacity: 0, x: -15 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.45, delay: 0.1 }}
                    >
                        {event.date}
                    </motion.div>

                    <motion.h3
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.5, delay: 0.18 }}
                    >
                        <Link to={event.slug}>{event.title}</Link>
                    </motion.h3>

                    <motion.p
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.5, delay: 0.26 }}
                    >
                        {event.description}
                    </motion.p>

                    <motion.div
                        className="time"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.4, delay: 0.34 }}
                    >
                        {event.time}{" "}
                        <i className="fal fa-long-arrow-right" />{" "}
                        <strong>Akash Institute of Pharmacy</strong>
                    </motion.div>
                </div>
            </div>
        </motion.div>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function Two() {
    const { ref: titleRef, inView: titleInView } = useInView({ threshold: 0.2, triggerOnce: true });
    const { ref: gridRef,  inView: gridInView  } = useInView({ threshold: 0.2, triggerOnce: true });
    const { ref: btnRef,   inView: btnInView   } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: underline wipe on section title */
    const underlineRef = useRef(null);
    useEffect(() => {
        if (titleInView && underlineRef.current) {
            gsap.fromTo(
                underlineRef.current,
                { scaleX: 0, transformOrigin: "center" },
                { scaleX: 1, duration: 0.8, ease: "power3.out", delay: 0.5 }
            );
        }
    }, [titleInView]);

    return (
        <section
            className="shop-area pt-120 pb-120 p-relative ss2"
        >
            <div className="container">

                {/* ── Section Title ── */}
                <div className="row">
                    <div className="col-lg-12 p-relative">
                        <motion.div
                            ref={titleRef}
                            className="section-title center-align mb-50 text-center"
                            variants={fadeDown}
                            initial="hidden"
                            animate={titleInView ? "visible" : "hidden"}
                        >
                            <h5><i className="fal fa-graduation-cap" /> Our Blogs</h5>
                            <h2 style={{ position: "relative", display: "inline-block" }}>
                                Latest from Our Blog
                                {/* GSAP underline wipe */}
                                <span
                                    ref={underlineRef}
                                    style={{
                                        display: "block",
                                        height: "3px",
                                        background: "var(--color-primary, #1E5AA8)",
                                        borderRadius: "2px",
                                        marginTop: "6px",
                                        transform: "scaleX(0)",
                                    }}
                                />
                            </h2>
                        </motion.div>
                    </div>
                </div>

                {/* ── Blog Cards — staggered ── */}
                <motion.div
                    ref={gridRef}
                    className="row"
                    variants={cardContainerVariants}
                    initial="hidden"
                    animate={gridInView ? "visible" : "hidden"}
                >
                    {events.map((event) => (
                        <BlogCard key={event.id} event={event} />
                    ))}
                </motion.div>

                {/* ── View All Button ── */}
                <motion.div
                    ref={btnRef}
                    className="slider-btn view-all-blogs mt-0"
                    variants={btnVariants}
                    initial="hidden"
                    animate={btnInView ? "visible" : "hidden"}
                >
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.97 }}
                        style={{ display: "inline-block" }}
                    >
                        <Link to="/blogs" className="btn ss-btn smoth-scroll">
                            View All Blogs <i className="fal fa-long-arrow-right" />
                        </Link>
                    </motion.div>
                </motion.div>

            </div>
        </section>
    );
}

export default Two;
